import { create } from 'zustand';
import type {
  TenantTheme,
  TenantContent,
  TenantConfig,
  TenantPreferences,
  HeaderFooterConfig,
  JobCategory,
} from '@/types/tenant.types';
import { preloadAuthImages } from '@/services/tenant/preloadAuthImages';

/**
 * Tenant store — mirrors Flutter's CommonService + LocalDataService
 * for tenant-specific configuration.
 */

interface TenantState {
  workspaceId: string;
  theme: TenantTheme | null;
  content: TenantContent | null;
  config: TenantConfig | null;
  preferences: TenantPreferences | null;
  mobilePreferences: Record<string, unknown> | null;
  headerFooterConfig: HeaderFooterConfig | null;
  isInitialized: boolean;
  isMaintenanceMode: boolean;

  setWorkspaceId: (id: string) => void;
  setTheme: (theme: TenantTheme) => void;
  setContent: (content: TenantContent) => void;
  setConfig: (config: TenantConfig) => void;
  setPreferences: (prefs: TenantPreferences) => void;
  setMobilePreferences: (prefs: Record<string, unknown>) => void;
  setHeaderFooterConfig: (config: HeaderFooterConfig) => void;
  setInitialized: () => void;

  getJobCategories: () => JobCategory[];
}

const DEFAULT_JOB_CATEGORIES: JobCategory[] = [
  { title: 'Department', key: 'departments' },
  { title: 'Skills', key: 'skills' },
  { title: 'Experience', key: 'experience' },
];

export const useTenantStore = create<TenantState>((set, get) => ({
  workspaceId: localStorage.getItem('workspaceId') ?? '',
  theme: null,
  content: null,
  config: null,
  preferences: null,
  mobilePreferences: null,
  headerFooterConfig: null,
  isInitialized: false,
  isMaintenanceMode: false,

  setWorkspaceId: (id) => {
    localStorage.setItem('workspaceId', id);
    set({ workspaceId: id });
  },

  setTheme: (theme) => {
    localStorage.setItem('tenantTheme', JSON.stringify(theme));
    set({ theme });
  },

  setContent: (content) => {
    localStorage.setItem('tenantContent', JSON.stringify(content));
    const isMaintenance = content?.isMaintainancePageVisible ?? false;
    set({ content, isMaintenanceMode: isMaintenance });
    preloadAuthImages(content);
  },

  setConfig: (config) => {
    localStorage.setItem('tenantConfig', JSON.stringify(config));
    set({ config });
  },

  setPreferences: (prefs) => {
    localStorage.setItem('tenantPreferences', JSON.stringify(prefs));
    set({ preferences: prefs });
  },

  setMobilePreferences: (prefs) => {
    localStorage.setItem('mobilePreferences', JSON.stringify(prefs));
    set({ mobilePreferences: prefs });
  },

  setHeaderFooterConfig: (config) => {
    localStorage.setItem('headerFooterConfig', JSON.stringify(config));
    set({ headerFooterConfig: config });
  },

  setInitialized: () => set({ isInitialized: true }),

  /**
   * Mirrors Flutter GenericHelpers.getJobCategories()
   */
  getJobCategories: () => {
    const prefs = get().preferences;
    if (prefs?.jobCategories && prefs.jobCategories.length > 0) {
      return prefs.jobCategories;
    }
    return DEFAULT_JOB_CATEGORIES;
  },
}));
